import { InventoryTypeKey } from '@carvana/merch-contracts/InventoryType';
import { Consumer } from '~/constants/common/consumer';
import OrderState from '~/constants/common/OrderState';
import { assetsCdnUrl, vexCdnUrl } from '~/constants/environment';
import optimizeImage from '~/utilities/optimizeImage';
import StatusCardType from './StatusCardType';

export function getCustomAttributesDescription(
  cardStatus: StatusCardType
): string {
  switch (cardStatus) {
    case StatusCardType.OrderPlaced:
      return 'Order placed cta';
    case StatusCardType.Purchased:
      return 'Purchased vehicle cta';
    case StatusCardType.Reserved:
      return 'Reserved vehicle cta';
    case StatusCardType.PurchaseInProgress:
      return 'Purchase in progress cta';
  }
}

export function getTitleText(
  cardStatus: StatusCardType,
  make: string,
  hasOpenVerifications: boolean
): string {
  switch (cardStatus) {
    case StatusCardType.OrderPlaced:
      return hasOpenVerifications
        ? `This ${make} is almost yours!`
        : `Congrats on your new ${make}!`;
    case StatusCardType.Purchased:
      return `Congrats on your new ${make}!`;
    case StatusCardType.Reserved:
      return `This ${make} is reserved just for you!`;
    case StatusCardType.PurchaseInProgress:
      return `This ${make} is almost yours!`;
  }
}

export function getCtaText(
  cardStatus: StatusCardType,
  shouldUseTitleCasing?: boolean
): string {
  switch (cardStatus) {
    case StatusCardType.OrderPlaced:
      return shouldUseTitleCasing
        ? 'View Purchase Details'
        : 'View purchase details';
    case StatusCardType.Purchased:
      return shouldUseTitleCasing
        ? 'View Vehicle Dashboard!'
        : 'View vehicle dashboard!';
    case StatusCardType.Reserved:
      return shouldUseTitleCasing ? 'Check Status' : 'Check status';
    case StatusCardType.PurchaseInProgress:
      return shouldUseTitleCasing ? 'Continue Purchase' : 'Continue purchase';
  }
}

export function getCtaLink(
  cardStatus: StatusCardType,
  vehicleId: number,
  consumer: Consumer
): string {
  switch (cardStatus) {
    case StatusCardType.OrderPlaced:
      return '/purchase';
    case StatusCardType.Purchased:
      return `/my-carvana/vehicle/${vehicleId}?referenceType=vehicle`;
    case StatusCardType.Reserved:
      return `/vehicle/${vehicleId}${consumer ? `?refSource=${consumer}` : ''}`;
    case StatusCardType.PurchaseInProgress:
      return '/purchase';
  }
}

export function getSubText(
  cardStatus: StatusCardType,
  hasOpenVerifications: boolean,
  orderState: OrderState
): string {
  switch (cardStatus) {
    case StatusCardType.OrderPlaced:
      return hasOpenVerifications
        ? ''
        : 'All of your tasks are complete! Sit back, relax and get excited for your new ride.';
    case StatusCardType.Purchased:
      return 'View your registration status, warranty details and more.';
    case StatusCardType.Reserved:
      return "We'll soon be inspecting and perfecting your new ride.";
    case StatusCardType.PurchaseInProgress:
      return orderState === OrderState.InProgressWithTradeIn
        ? 'You have a few more steps to complete your trade-in before time runs out!'
        : 'You have a few more steps to complete your purchase before time runs out!';
  }
}

type ImageProps = {
  imageUrl: string;
  imageWidth?: number;
  inventoryType: InventoryTypeKey;
  stockNumber: number;
};

export function getImageSrc({
  imageUrl,
  imageWidth = 500,
  inventoryType,
  stockNumber
}: ImageProps) {
  const isWip = inventoryType === InventoryTypeKey.WIP;
  if (!imageUrl) {
    return optimizeImage(`${assetsCdnUrl}/search-assets/noevox.png`, {
      format: 'auto',
      quality: 90
    });
  }

  return isWip
    ? imageUrl
    : optimizeImage(`${vexCdnUrl}/${stockNumber}/standardizedHero.jpg`, {
        crop: '16:9',
        format: 'auto',
        width: imageWidth
      });
}
